/* eslint-disable @typescript-eslint/naming-convention */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { HotToastService } from '@ngneat/hot-toast';
import { badgeColorCode, BadgeOptions } from 'app/components/badge/badge.interface';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { LocalisationService } from 'app/core/services/localisation/zuper.localisation.service';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { PickerSelectOptions } from '../data-picker.interface';
import _ from 'lodash';

@Component({
  selector: 'request-data-picker',
  templateUrl: './request-data-picker.component.html',
  styleUrls: ['./request-data-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class RequestDataPickerComponent implements OnInit, OnChanges {
  @Input() filter: any = null;
  @Input() selectOption: PickerSelectOptions = PickerSelectOptions.select;
  @Output() eventEmitter: EventEmitter<any> = new EventEmitter();
  @Input() selectedRequests: any[] = [];
  @Input() searchKey: string = '';
  @Input() pickerConfig: any = null;
  @Input() requestUrl: any = '';
  @Input() pageIndex: number = 0;
  @Input() showSelectedItems: boolean = false;
  @Input() sortBy: string = 'created_at';
  @Input() sortType: string = 'DESC';
  @Input() requestData: any[] = null;
  @Input() customParams: any;
  @Input() filterFormGroup: UntypedFormGroup;
  @Input() hasFilterValue: boolean = false;

  duplicatedRequestData: any[] = [];
  requests: any[] = [];
  requestsList: any[] = [];
  loaded: boolean = false;
  defaultParamObj: any = {
    'sort': this.sortType,
    'sort_by': this.sortBy,
    'filter.is_active': true
  };
  requestEleId: string;
  requestDetails: any = null;
  onSearch: boolean = true;
  localeKeys: any = {
    expand: 'expand',
    collapse: 'collapse'
  };
  isSpinLoading: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private dataCollectorService: DataCollectorService,
    private localisationService: LocalisationService,
    private changeDetectorRef: ChangeDetectorRef,
    private toast: HotToastService) { }

  ngOnInit(): void {
    this.localisationService.setDefaultLanguage();
    if (this.selectOption && this.selectOption === PickerSelectOptions.readOnly) {
      delete this.defaultParamObj?.['filter.is_active'];
    }
    this.filterFormGroup?.valueChanges.subscribe((data: any) => {
      this.updateDataParams(data);
    });
    this.showDataPickerPanel();
  }

  getRequestStatusOptions(status: any): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: status?.status_name,
      color: status?.status_color || badgeColorCode[status?.status_type],
      displayName: status?.status_name,
      extraClass: 'capitalize'
    };
    return badgeOption;
  }

  getTagBadgeOptions(tag: string): BadgeOptions {
    const badgeOption: BadgeOptions = {
      type: tag,
      displayType: tag,
      color: badgeColorCode[tag],
      displayKey: tag,
      displayName: tag
    };
    return badgeOption;
  }

  rotateArrowIcon(requestUID: any): any {
    return {
      'rotate-arrow-icon-down': this.isExpandingAction(requestUID),
      'rotate-arrow-icon-up': !this.isExpandingAction(requestUID)
    };
  }

  showSpinLoading(requestUID: any): boolean {
    return this.isExpandingAction(requestUID) && this.isSpinLoading;
  }

  updateArrowTooltipText(requestUID: any): string {
    return this.isExpandingAction(requestUID) ? this.localeKeys.collapse : this.localeKeys.expand;
  }

  isExpandingAction(requestUID: any): boolean {
    return (this.requestEleId === 'request_details_' + requestUID);
  }

  viewRequestFullDetails(requestUID: string): string {
    return '/request/' + requestUID + '/details';
  }

  viewRequestDetails(event: any, requestUID: string): void {
    event.stopPropagation();
    const elementId: string = 'request_details_' + requestUID;
    if (this.requestEleId === elementId) {
      this.resetRequestDetails();
    } else {
      this.requestEleId = elementId;
      this.getRequestDetails(requestUID);
    }
  }

  getRequestDetails(requestUID: string): void {
    this.isSpinLoading = true;
    /* eslint-disable quotes */
    this.dataCollectorService.getData(this.requestUrl + `/` + requestUID).subscribe(
      /* eslint-enable quotes */
      (requests: any) => {
        this.requestDetails = requests.data;
        this.isSpinLoading = false;
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.resetRequestDetails();
        this.isSpinLoading = false;
      });
  }

  resetRequestDetails(): void {
    this.requestEleId = '';
    this.requestDetails = null;
    this.changeDetectorRef.detectChanges();
  }

  isSelectedRequest(request: any): boolean {
    if (this.selectedRequests?.length) {
      return this.selectedRequests.find(value => value.request_uid === request.request_uid) ? true : false;
    }
    return false;
  }

  selectRequest(event: any, request: any): void {
    if (this.selectOption === PickerSelectOptions.multiSelect) {
      if (event.checked === true) {
        this.selectedRequests.push(request);
      } else {
        this.selectedRequests = this.selectedRequests.filter(value => value.request_uid !== request.request_uid);
      }
    } else {
      this.selectedRequests = [request];
    }
    this.eventEmitter.emit({ value: this.selectedRequests });
  }

  triggerSearch(searchKey: any): void {
    this.onSearch = true;
    if (this.requestData) {
      this.requestDataSearch(searchKey);
    } else {
      this.requestRemoteDataSearch(searchKey);
    }
  }

  requestDataSearch(searchKey: any): void {
    if (!searchKey) {
      this.requests = this.duplicatedRequestData;
    } else {
      const filteredValue: any[] = this.duplicatedRequestData?.filter((request: any) => {
        searchKey = searchKey.toLowerCase();
        const requestId: string = request?.request_id?.toString()?.toLowerCase();
        const requestTitle: string = request?.request_title?.toString()?.toLowerCase();
        const requestDescription: string = request?.request_description?.toString()?.toLowerCase();
        if (requestId?.indexOf(searchKey) > -1 || requestTitle?.indexOf(searchKey) > -1 || requestDescription?.indexOf(searchKey) > -1) {
          return request;
        }
      });
      this.requests = filteredValue?.length ? [...filteredValue] : [];
    }
  }

  requestRemoteDataSearch(searchKey: any): void {
    delete this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey];
    if (this.defaultParamObj[this.pickerConfig.params.searchKey] !== searchKey) {
      const pageIndex: number = 0;
      this.defaultParamObj[this.pickerConfig.params.searchKey] = searchKey;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  sortingAction(key: string, value: string): void {
    if (value) {
      const pageIndex: number = this.pageIndex ?? 0;
      this.defaultParamObj[key] = value;
      this.getData(pageIndex);
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
  }

  getData(pageIndex: number, isOpen?: boolean): void {
    this.requestEleId = '';
    this.loaded = false;
    this.defaultParamObj.page = pageIndex + 1;
    const urlParams = this.utilityService.constructParam(this.defaultParamObj);

    this.dataCollectorService.getData(this.requestUrl, urlParams).subscribe(
      (response: any) => {
        this.requestsList = response.data;
        this.requests = response.data;
        this.loaded = true;
        this.eventEmitter.emit({ totalRecords: response.total_records });
        if (isOpen && this.filter && this.requests.length) {
          this.selectedRequests = this.selectedRequests?.length > 0 ? _.uniqBy([...this.selectedRequests, ...this.filter], 'request_uid') : this.filter;
          this.eventEmitter.emit({ value: this.selectedRequests });
        } else if (isOpen && this.requests.length === 0) {
          this.onSearch = false;
        }
        this.changeDetectorRef.detectChanges();
      }, (_error: any) => {
        this.loaded = true;
      });
  }

  showDataPickerContent(): void {
    if (this.hasFilterValue) {
      return null;
    }
    const pageIndex: number = this.pageIndex ?? 0;
    Object.keys(this.pickerConfig.params).forEach((key: any) => {
      const paramKey: any = this.pickerConfig.params[key];
      if (key !== 'uniqueSearchKey') {
        let value: any = '';
        if (key === 'countKey') {
          value = 10;
        } else if (key === 'pageKey') {
          value = 1;
        } else if (key === 'searchKey' && !this.showSelectedItems && this.searchKey) {
          value = this.searchKey;
        }
        this.defaultParamObj[paramKey] = value;
      }
    });
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    if (this.filter) {
      this.updateFilterValue();
      this.eventEmitter.emit({ pageIndex: pageIndex });
    }
    this.eventEmitter.emit({ sortBy: this.defaultParamObj['sort_by'], sortType: this.defaultParamObj['sort'] });
    this.getData(pageIndex, true);
  }

  updateFilterValue(): void {
    if (this.selectOption === PickerSelectOptions.select && this.filter.length > 0) {
      if (this.filter[0].request_title) {
        this.defaultParamObj[this.pickerConfig.params.searchKey] = this.filter[0].request_title;
        this.eventEmitter.emit({ searchKey: this.filter[0].request_title });
      } else {
        this.defaultParamObj[this.pickerConfig.params.uniqueSearchKey] = this.filter[0].request_uid;
      }
    }
  }

  updateDataParams(formControlValues: any): void {
    if (this.customParams && Object.keys(this.customParams).length) {
      this.defaultParamObj = { ...this.defaultParamObj, ...this.customParams };
    }
    const params: any = this.defaultParamObj ?? {};
    if (formControlValues.moduleBasedFilterValue) {
      formControlValues.moduleBasedFilterValue.forEach((value: any) => {
        if (value.value) {
          params[value?.key] = value.value;
        } else {
          delete params[value?.key];
        }
      });
    }
    this.updateFilters();
  }

  updateFilters(): void {
    const pageIndex: number = 0;
    this.getData(pageIndex, true);
    this.eventEmitter.emit({ pageIndex: pageIndex });
  }

  updateRequestData(): void {
    this.requests = this.requestData;
    this.duplicatedRequestData = [...this.requestData];
    this.onSearch = false;
    this.loaded = true;
    if (this.filter && this.requestData?.length) {
      this.selectedRequests = this.filter;
      this.eventEmitter.emit({ value: this.selectedRequests });
    }
    if (this.searchKey) {
      this.eventEmitter.emit({ searchKey: this.searchKey });
      this.triggerSearch(this.searchKey);
    }
  }

  resetRequestDataPicker(): void {
    this.requests = [];
  }

  showDataPickerPanel(): void {
    this.resetRequestDataPicker();
    if (this.requestData) {
      this.updateRequestData();
    } else if (this.showSelectedItems) {
      this.requests = this.selectedRequests;
    } else if (this.requestsList.length === 0 && this.searchKey) {
      this.showDataPickerContent();
    } else if (this.requestsList.length > 0 || this.searchKey) {
      this.requests = this.requestsList;
    } else {
      this.showDataPickerContent();
    }
  }

  hideSelectedItemsPanel(): void {
    this.eventEmitter.emit({ showSelectedItems: false, value: this.selectedRequests, filter: this.selectedRequests });
  }

  getBadgeOptions(): BadgeOptions {
    return {
      type: 'PRIMARY',
      displayKey: null,
      displayName: this.selectedRequests ? this.selectedRequests.length : 0,
      displayType: 'listingCount'
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.searchKey?.firstChange === false) {
      this.triggerSearch(changes?.searchKey?.currentValue);
    }

    if (changes?.pageIndex?.previousValue !== undefined) {
      this.getData(changes?.pageIndex?.currentValue);
    }

    if (changes?.showSelectedItems?.firstChange === false || changes?.requestData?.firstChange === false) {
      this.showDataPickerPanel();
    }

    if (changes?.sortBy?.firstChange === false && changes?.sortBy?.previousValue !== undefined) {
      this.sortingAction('sort_by', changes?.sortBy?.currentValue);
    }

    if (changes?.sortType?.firstChange === false && changes?.sortType?.previousValue !== undefined) {
      this.sortingAction('sort', changes?.sortType?.currentValue);
    }
  }
}
